import React, { useState, useEffect, useMemo } from 'react';
import { PriceList, ContractLine } from '../types';
import Modal from './Modal';

interface DiscountInfo {
  type: 'percentage' | 'fixed';
  percentageValue: number;
  fixedValue: number;
}

interface ChangePlanModalProps {
  contractLines: ContractLine[];
  priceLists: PriceList[];
  onSubmit: (updates: { contractLineId: string; priceListId: number; discount?: { type: string; value: number } }[]) => void;
  onCancel: () => void;
  singleLine?: ContractLine;
}

const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  contractLines,
  priceLists,
  onSubmit,
  onCancel,
  singleLine
}) => {
  const [selectedPriceLists, setSelectedPriceLists] = useState<{ [key: string]: number }>({});
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discounts, setDiscounts] = useState<{ [key: string]: DiscountInfo }>({});

  // Filter lines to change
  const linesToChange = useMemo(() => (singleLine ? [singleLine] : contractLines), [singleLine, contractLines]);

  useEffect(() => {
    // Initialize selected price lists with current values
    const initial: { [key: string]: number } = {};
    const initialDiscounts: { [key: string]: DiscountInfo } = {};
    
    linesToChange.forEach(line => {
      initial[line.contractline_id] = line.pricelist_id;
      
      // Initialize discount info for each line
      initialDiscounts[line.contractline_id] = {
        type: 'percentage',
        percentageValue: 0,
        fixedValue: 0
      };
    });
    
    setSelectedPriceLists(initial);
    setDiscounts(initialDiscounts);
  }, [linesToChange]);

  // Filter price lists based on billing cycle compatibility
  const getCompatiblePriceLists = (contractLine: ContractLine) => {
    const billingCycle = contractLine.billing_cycle;
    
    // Strict mapping: monthly contracts can only have monthly pricelists, yearly can only have yearly
    const cycleMapping: { [key: string]: string[] } = {
      '1_month': ['1_month'],
      '3_months': ['3_months'],
      '6_months': ['6_months'],
      '12_months': ['12_months'],
      '24_months': ['24_months'],
    };

    const compatiblePeriods = cycleMapping[billingCycle] || [];
    
    return priceLists.filter(pl => 
      pl.is_active && 
      compatiblePeriods.includes(pl.billing_period)
    );
  };

  const handlePriceListChange = (contractLineId: string, priceListId: number) => {
    setSelectedPriceLists(prev => ({
      ...prev,
      [contractLineId]: priceListId
    }));
  };

  const calculateDiscountValues = (lineId: string, type: 'percentage' | 'fixed', value: number) => {
    const priceListId = selectedPriceLists[lineId];
    const priceList = priceLists.find(pl => pl.pricelist_id === priceListId);
    
    if (!priceList) return;
    
    const baseAmount = priceList.amount;
    let percentageValue = 0;
    let fixedValue = 0;
    
    if (type === 'percentage') {
      percentageValue = value;
      fixedValue = (baseAmount * value) / 100;
    } else {
      fixedValue = value;
      percentageValue = (value / baseAmount) * 100;
    }
    
    setDiscounts(prev => ({
      ...prev,
      [lineId]: {
        type,
        percentageValue: Math.round(percentageValue * 100) / 100,
        fixedValue: Math.round(fixedValue * 100) / 100
      }
    }));
  };

  const handleSubmit = () => {
    const updates = Object.entries(selectedPriceLists)
      .filter(([lineId, priceListId]) => {
        const line = linesToChange.find(l => l.contractline_id === lineId);
        return line && line.pricelist_id !== priceListId;
      })
      .map(([contractLineId, priceListId]) => {
        const discount = discounts[contractLineId];
        return {
          contractLineId,
          priceListId,
          discount: applyDiscount && discount ? {
            type: discount.type,
            value: discount.type === 'percentage' ? discount.percentageValue : discount.fixedValue
          } : undefined
        };
      });

    if (updates.length === 0) {
      alert('No changes were made');
      return;
    }

    onSubmit(updates);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <Modal
      title={singleLine ? 'Change Plan' : 'Bulk Change Plans'}
      onClose={onCancel}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h4>Select New Plans</h4>
          {linesToChange.map(line => {
            const compatiblePriceLists = getCompatiblePriceLists(line);
            const currentPriceList = priceLists.find(pl => pl.pricelist_id === line.pricelist_id);
            
            return (
              <div key={line.contractline_id} style={{ 
                marginBottom: '15px', 
                padding: '15px', 
                border: '1px solid #ddd',
                borderRadius: '5px'
              }}>
                <h5>{line.contractline_name}</h5>
                <p style={{ fontSize: '0.9em', color: '#666' }}>
                  Current Plan: {currentPriceList?.name || 'Unknown'} - 
                  {currentPriceList && formatCurrency(currentPriceList.amount, currentPriceList.currency)}
                </p>
                
                <select
                  value={selectedPriceLists[line.contractline_id] || ''}
                  onChange={(e) => handlePriceListChange(line.contractline_id, parseInt(e.target.value))}
                  className="form-control"
                  style={{ marginTop: '10px' }}
                >
                  <option value="">Select a plan...</option>
                  {compatiblePriceLists.map(pl => (
                    <option key={pl.pricelist_id} value={pl.pricelist_id}>
                      {pl.name} - {formatCurrency(pl.amount, pl.currency)} ({pl.billing_period})
                    </option>
                  ))}
                </select>
                
                {compatiblePriceLists.length === 0 && (
                  <p style={{ color: '#e74c3c', marginTop: '10px' }}>
                    No compatible plans found for {line.billing_cycle} billing cycle
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {singleLine && (
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa',
            borderRadius: '5px'
          }}>
            <h4>Apply Discount (Optional)</h4>
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={applyDiscount}
                  onChange={(e) => setApplyDiscount(e.target.checked)}
                  style={{ marginRight: '10px' }}
                />
                Apply discount
              </label>
            </div>
            
            {applyDiscount && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label>Relative Discount (%):</label>
                  <input
                    type="number"
                    value={discounts[singleLine.contractline_id]?.percentageValue || 0}
                    onChange={(e) => calculateDiscountValues(
                      singleLine.contractline_id, 
                      'percentage', 
                      parseFloat(e.target.value) || 0
                    )}
                    className="form-control"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <small>Percentage off the base price</small>
                </div>
                
                <div>
                  <label>Absolute Discount ({priceLists.find(pl => pl.pricelist_id === selectedPriceLists[singleLine.contractline_id])?.currency || 'EUR'}):</label>
                  <input
                    type="number"
                    value={discounts[singleLine.contractline_id]?.fixedValue || 0}
                    onChange={(e) => calculateDiscountValues(
                      singleLine.contractline_id, 
                      'fixed', 
                      parseFloat(e.target.value) || 0
                    )}
                    className="form-control"
                    min="0"
                    step="0.01"
                  />
                  <small>Fixed amount off the base price</small>
                </div>
              </div>
            )}
          </div>
        )}

        {!singleLine && (
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa',
            borderRadius: '5px'
          }}>
            <h4>Bulk Discount (Optional)</h4>
            <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>
              Apply the same discount to all selected contract lines
            </p>
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={applyDiscount}
                  onChange={(e) => setApplyDiscount(e.target.checked)}
                  style={{ marginRight: '10px' }}
                />
                Apply bulk discount
              </label>
            </div>
            
            {applyDiscount && (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <label>Discount Type:</label>
                  <select
                    onChange={(e) => {
                      const type = e.target.value as 'percentage' | 'fixed';
                      linesToChange.forEach(line => {
                        const currentDiscount = discounts[line.contractline_id];
                        calculateDiscountValues(
                          line.contractline_id,
                          type,
                          type === 'percentage' ? currentDiscount?.percentageValue || 0 : currentDiscount?.fixedValue || 0
                        );
                      });
                    }}
                    className="form-control"
                    defaultValue="percentage"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                
                <div>
                  <label>Discount Value:</label>
                  <input
                    type="number"
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      linesToChange.forEach(line => {
                        const discount = discounts[line.contractline_id];
                        calculateDiscountValues(line.contractline_id, discount?.type || 'percentage', value);
                      });
                    }}
                    className="form-control"
                    min="0"
                    step="0.01"
                  />
                  <small>This value will be applied to all selected lines</small>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSubmit}>
          Apply Changes
        </button>
      </div>
    </Modal>
  );
};

export default ChangePlanModal;